import moment from 'moment';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { get, has, isEmpty } from 'lodash';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { AppointmentService } from '../../services/appointment.service';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { IKeyElement, ITablelistAction, ITablelistExecute } from '../../interfaces';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { SearchService } from '@ispm/dynamic-form/services/search.service';
import { SimpleNotesComponent } from '../../../notes/components/simple-notes.component/simple-notes.component';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { WorklistService } from '../../services/worklist.service';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { selectCurrentUserSpecialities } from '@ispm/user/store/user.selector';
import { PatientService } from '@ispm-client/modules/patient/services/patient.service';
import {EXPORT_DATA_STORAGE_KEY} from "@ispm/shared/services/ui-customization.service";
import {SessionStorageService} from "@ispm/shared/services/session-storage.service";

@Component({
  selector: 'gen-worklist-item',
  templateUrl: './worklist-item.component.html',
  styleUrls: ['./worklist-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorklistItemComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() keys: IKeyElement[];
  @Input()
  set value(value: any) {
    this._value = value.rowData;
    this._worklistType = value.worklistType;
    this.getParsedValue();
    if (value.discipline) {
      this.itemDisciplineSubject.next(value.discipline && value.discipline.code || '');
    }
  }
  get value(): any {
    return this._value;
  }
  @Input() searchText: string;
  @Input() currentUserId: string;
  @Input() practitionerType: string;
  @Input() type: string;
  @Input() trackByField: string;
  @Input() injectedActions: Array<ITablelistAction>;
  @Input() worklistActions: any;
  @Input() disabledworklistActions: any;
  @Input() mode: any;
  @Input() executeActions: Array<ITablelistExecute>;
  @Input() checkActionsPermission: boolean = true;
  @Output() noteCompletedEmit: EventEmitter<any> = new EventEmitter();

  public readonly CustomBooleanIndicationFalseClass: string = 'falseClass';
  public readonly CustomBooleanIndicationNoneClass: string = 'noneClass';
  public readonly CustomBooleanIndicationTrueClass: string = 'trueClass';
  public readonly IsMainKey: string = 'isMainKey';

  public noteTooltipMaxHeight: number;
  public noteTooltipMaxWidth: number;
  public showActions$: Observable<boolean>;
  public showMultiTooltip: boolean = false;
  public valuesParsed: Array<{
    type: string;
    subType: string;
    value: string[];
    payload: string;
    payloadData: any;
    className?: string,
    payloadValue?: any;
    executeActions?: ITablelistExecute[];
  }>;
  private _destroyer$: Subject<any> = new Subject<any>();
  private _worklistType: string;
  private _value: string;
  private modalSubscriber$: Subscription;
  private itemDisciplineSubject: BehaviorSubject<string> = new BehaviorSubject('');
  private itemDiscipline$ = this.itemDisciplineSubject.asObservable();
  private discipline: any;

  constructor(
    private worklistService: WorklistService,
    private dynamicModalLoader: DynamicModalLoaderService,
    private appointmentService: AppointmentService,
    private elementRef: ElementRef,
    private store: Store<IgenomicsState>,
    private searchService: SearchService,
    private changeDetectorRef: ChangeDetectorRef,
    private patientService: PatientService
  ) {
  }

  ngOnInit() {
    if (moment(this.searchText, 'YYYY[-]MM[-]DD', true).isValid()) {
      this.searchText = moment(this.searchText).format('DD MMM YYYY');
    }

    this.getParsedValue();
    this.discipline = this.getValueByKey('discipline');

    this.showActions$ = this.store.pipe(takeUntil(this._destroyer$.asObservable()),
      select(selectCurrentUserSpecialities),
      map((specialities) => {
          // there are worklist items, which doesn't contain discipline code, for example PNC worklist item, for such items we should return true value
          if (this.checkActionsPermission) {
            return !this.discipline || specialities.length === 0 ? true : specialities.some(speciality => get(speciality, 'code', undefined) === this.discipline.code);
          } else {
            return true;
          }
        }
      ),
      startWith(true)
    );
  }

  ngAfterViewInit(): void {
    if (this.valuesParsed && this.valuesParsed.find(value => value.type === 'note')) {
      const boundingClientRect = this.elementRef.nativeElement.parentNode.parentNode.getBoundingClientRect();
      this.noteTooltipMaxHeight = boundingClientRect.height * 2;
      this.noteTooltipMaxWidth = boundingClientRect.width;
    }
  }

  ngOnDestroy(): void {
    this.keys =[];
    this._destroyer$.next();
    this._destroyer$.complete();
  }
  openNotePopup(event, value, payload) {
    event.stopPropagation();
    if (this.modalSubscriber$) {
      return;
    }
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent,

      {
        componentToLoad: SimpleNotesComponent,
        classes: 'simple-note-modal-container ',
        title: 'Note',
        payload: {
          notes: this.keys[0].directValue ? payload['value'] : value
        },
        // approveBtn: 'Save',
        declineBtn: 'Close'
      }, ModalTypes.CUSTOM)
      .filter(response => response.payload)
      .map(response => response.payload).pipe(takeUntil(this._destroyer$.asObservable()))
      .subscribe((response: any) => {
          switch (<modalResponse> response.status) {
            case modalResponse.approve:
              break;
            case modalResponse.decline:
            default:
              return false;
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        },
        () => {
        },
        () => {
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        });
  }

  escapeChevron(val: string | string[]): string {
    return StringUtils.escapeChevron(val);
  }

  getValueByKey(key: string, fallbackValue: string = '', subKey?: string) {
    try {
      const value = get(this.value, key);
      if (typeof value === 'boolean' && value) {
        return fallbackValue || '';
      } else if (subKey && Array.isArray(value)) {
        return value.map(val => get(val, subKey));
      } else {
        return value;
      }
    } catch (e) {
      return null;
    }
  }

  noteCompleted($event: any) {
    this.noteCompletedEmit.emit(true);
  }

  trackByFn(index, item: string) {
    return item;
  }

  isCustomBooleanIndication(parsedValue: any): boolean {
    const payloadData = parsedValue.payloadData;
    return parsedValue.type === 'boolean-indication'
      && payloadData
      && (payloadData.hasOwnProperty(this.CustomBooleanIndicationTrueClass)
        || payloadData.hasOwnProperty(this.CustomBooleanIndicationFalseClass));
  }

  getCustomBooleanIndicationClass(parsedValue: any): string {
    const data = parsedValue.payloadData;
    const value = parsedValue.originValue;
    let c: string;
    if (value === true) {
      c = data[this.CustomBooleanIndicationTrueClass];
    } else if (value === false) {
      c = data[this.CustomBooleanIndicationFalseClass];
    } else {
      c = data[this.CustomBooleanIndicationNoneClass];
    }
    return c ? c : '';
  }

  shouldHideValue(parsedValue: any): boolean {
    return this.isCustomBooleanIndication(parsedValue) && this.getCustomBooleanIndicationClass(parsedValue) === '';
  }

  isMainValue(parsedValue: any, i: number): boolean {
    return (parsedValue && parsedValue.hasOwnProperty(this.IsMainKey)) ?
      parsedValue[this.IsMainKey] : (i === 0);
  }

  private getParsedValue() {
    this.valuesParsed = this.keys.map(keyElement => {
      const payload = keyElement.value || { 'subKey': null, 'classKey': null};
      let payloadValue, payloadData;
      if (typeof payload === 'string') {
        payloadValue = payload;
      }
      else {
        payloadValue = payload['value'];
        payloadData = this.handlePayloadData(keyElement.type, payload['data'], payloadValue, keyElement.key, keyElement);
      }
      let value = this.getValueByKey(keyElement.key, payloadValue);
      let originValue =  get(this._value, keyElement.key);
      if (keyElement.hasOwnProperty('falseKey')) {
        const val  =  get(this._value, [keyElement.falseKey]);
        originValue =  get(val, keyElement.key);
        value = originValue;
      }
      if (this.valueNotFound(value)) {
        if (keyElement.fallbackKey) {
          value = this.getValueByKey(keyElement.fallbackKey, payloadValue);
          if (this.valueNotFound(value)) {
            value = this.updateToDefaultValue(keyElement);
          }
        } else {
          value = this.updateToDefaultValue(keyElement);
        }
      }
      if (keyElement.hasOwnProperty('fallbackKey') && !isEmpty(payloadData) && payloadData.hasOwnProperty('workFlowStatus') && payloadData['workFlowStatus'] === true) {
        const val  =  get(this._value, [keyElement.fallbackKey], '');
        originValue =  get(val, keyElement.key, '');
        if (isEmpty(originValue) && keyElement.hasOwnProperty('fallbackConfigWhenNoLungRads')) {
          const fallbackConfigWhenNoLungRads = keyElement.fallbackConfigWhenNoLungRads;

          const matchFromFieldPathObject = get(this._value, [fallbackConfigWhenNoLungRads.matchFromFieldPath], '');
          const matchFromKeyValue = get(matchFromFieldPathObject, [fallbackConfigWhenNoLungRads.matchFromKey], '');

          const matchToFieldPathArray = get(this._value, [fallbackConfigWhenNoLungRads.matchToFieldPath], '');
          const referredValue = matchToFieldPathArray ? matchToFieldPathArray.find(val => val[fallbackConfigWhenNoLungRads.matchToKey] <= matchFromKeyValue): '';

          value = get(referredValue, keyElement.key, '');
        } else {
          value = originValue;
        }
      }
      const payloadClass = payload && payload['class'] ? payload['class'] : '';
      payloadValue = this.getTextFromHtml(payloadValue || value);
      const parsed = this.prepareValue(value, payloadClass, payload.subKey, payload.classKey);
      const automationClassName = 'AUTO_LIST_' + keyElement.key.split('.').join('_');
      if (parsed.className === 'brackets ' && parsed.value === '') {
        parsed.className = '';
      }

      let itemExecuteActions = [];
      if (!!this.executeActions && keyElement.type === 'execute') {
        // get action from this table by work item key
        itemExecuteActions = this.executeActions.filter(action => action.key === keyElement.key);
      }

      const parsedValue = {
        'automationClassName': automationClassName,
        'value':       parsed.value,
        'originValue': originValue,
        'className':   parsed.className,
        'type':        keyElement.type,
        'subType':     payload.subType,
        'payload':     payloadValue,
        'payloadData': payloadData,
        'payloadValue': payload,
        'executeActions': itemExecuteActions
      };
      if (keyElement.hasOwnProperty(this.IsMainKey)) {
        parsedValue[this.IsMainKey] = keyElement[this.IsMainKey];
      }

      if (['date', 'datetime'].includes(keyElement.type) && !isEmpty(parsedValue.value)) {
        // fixes GEN-177293 -> another date format issue
        parsedValue.value = parsedValue.value.replace(/UTCH:UTCM$/, '');
        // fixes GEN-107862 -> wrong date format issue
        parsedValue.value = moment(parsedValue.value).format();
      }
      return parsedValue;
    })
      .filter(item => !!item.value || ['actions', 'text', 'note', 'boolean-indication', 'execute', 'appointment'].indexOf(item.type) > -1);
  }

  private getTextFromHtml(value): string {
    return this.worklistService.getTextFromHtml(value);
  }

  private getClassByRowData(rowDataToCheck: any): string {
    let cssClass = '';
    rowDataToCheck.forEach(descr => {
      if (has(this._value, descr.property)) {
        switch (descr.condition) {
          case 'startExceed':
            cssClass = this.appointmentService.checkStartExceeds(get(this._value, descr.property)) ? descr.cssClass : '';
            break;
        }
      }

    });
    return cssClass;
  }

  private prepareValue(value: any, className: string, subKey?: string, classKey?: string) {
    let returnValue = {
      className: '',
      value:     value
    };
    switch (typeof value) {
      case 'object':
        className = value && value.hasOwnProperty(classKey)
          ? `${className} ${value[classKey]}`
          : (className || '');
        if (Array.isArray(value)) {
          returnValue = {
            value:     value.map(val => this.prepareValue(val, className, subKey, classKey))
              .filter(val => !!val.value),
            className: className
          };
        } else if (value) {
          returnValue = {
            className: className,
            value:     subKey ? value[subKey] : value
          };
        }
        break;
      case 'string':
        className = `${className} ${this.formalizeClassName(value)}`;
        returnValue = {
          className: className,
          value:     subKey ? value[subKey] : value
        };
        break;
      case 'boolean':
        className = `${className}`;
        returnValue = {
          className: className,
          value:     subKey ? value[subKey] : value
        };
        break;
    }
    return returnValue;
  }

  private formalizeClassName(className: string = ''): string {
    return className
      ? className.split(' ').join('_').toLowerCase()
      : '';
  }

  private handlePayloadData(type: string, payloadElement: any = { }, payloadValue?: any, key?: string, keyElement?: any) {
    let extraCssClass = ' ';
    let isWorkflowCompleted = false;

    const rowDataToCheck = get(payloadElement, 'rowDataToCheck', undefined);

    if (payloadElement.hasOwnProperty('fieldEmptyCheck')) {
      const fieldEmptyCheck = get(payloadElement, 'fieldEmptyCheck');
      const fieldPathToCheck = get(fieldEmptyCheck, 'fieldPathToCheck');
      const values: Array<any> = get(fieldEmptyCheck, 'valuesToCheck');

      if (values.includes(get(this.value, fieldPathToCheck, ''))) {
        extraCssClass = get(fieldEmptyCheck, 'cssClass');
      }
    }

    if (payloadElement.hasOwnProperty('workflowCompletedCheck')) {
      const workflowCompletedCheck = get(payloadElement, 'workflowCompletedCheck');
      const workflowPathToCheck = get(workflowCompletedCheck, 'fieldPathToCheck');
      const workflowValuesToCheck: Array<any> = get(workflowCompletedCheck, 'valuesToCheck');
      if (workflowValuesToCheck.includes(get(this.value, workflowPathToCheck, ''))) {
        isWorkflowCompleted = true;
      }
    }
    /* GEN-149056 - Using 'if' to keep type as 'Text' for DEFAULT worklist Template
    which is also responsible for generating excel
    if Type is different then excel won't export column gets filled with blank.
    CASE FOR : Template : worklistTemplate_AllFields_3.0.10_DEFAULT.taskDispatchTemplate.json
     */
    if (type === 'text' && has(keyElement , 'secKey')) {
      type = 'arrayContentIcon';
    }

    switch (type) {
      case 'note':
        const returnObject = {
          titleConfig:  {
            main:     {
              title:    null,
              subtitle: null
            },
            fallback: {
              title:    null,
              subtitle: null
            }
          },
          contextQuery: null,
          contextPaths: null,
          queryType: null,
          notes: [],
          activity: [],
          directData: false,
          revisionPath: '',
          nonAberation: undefined
        };
        returnObject.titleConfig.main.title = payloadElement.titlePath;
        returnObject.titleConfig.main.subtitle = payloadElement.subTitlePath;
        returnObject.nonAberation = payloadElement.nonAberation;
        returnObject.queryType = payloadElement.queryType;
        returnObject.directData = get(payloadElement, 'directData', false);
        returnObject.revisionPath = get(payloadElement, 'revisionPath', '');

        if ( returnObject.directData) {
          returnObject.notes = get(this.value, payloadElement.notesPath, []);
          returnObject.activity = get(this.value, payloadElement.activityPath, []);
        }

        returnObject.contextPaths = payloadElement.contextPaths;
        returnObject.contextQuery = payloadElement.contextPaths
          .reduce((arr, path) => {
            const context = get(this.value, path);
            if (context) {
              arr.push(context);
            }
            return arr;
          }, []);
        return returnObject;
      case 'date':
      case 'datetime':
        if (extraCssClass !== ' ') {
          return {
            className: extraCssClass
          };
        } else {
          const value = this.getValueByKey(key, payloadValue);
          if (payloadElement && value) {
            const returnObject = {
              className: extraCssClass
            };

            /*

              Example below due date code:

              today 25 Aug 2023T14:30:00 GMT+5:30
              Due date 25 Aug 2023T05:30:00 GMT+5:30

              today is compared with the dueDate in the below scenarios
              Diff in time :This calculated as today subtracted with dueDate , In above scenario, it would be +ve integer

              Check scenarios:
              RED: today > Due date  /* Here we would have a true condition
              YELLOW: today <= Due date and it should be less than 6 days of diff which is calculated as diff in days (Difference_In_Days)
              Black:  This is default when other conditions don't match .
              NOTE: setHours (0,0,0,0) for (today) and (parsedate) so that we can avoid the above senario.

            */


            const today = new Date();
            const parsedDate = new Date(value);
            const Difference_In_Time = parsedDate.getTime() - today.getTime();
            const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24);
            today.setHours(0, 0, 0, 0);
            parsedDate.setHours(0, 0, 0, 0);
            if (payloadElement.hasOwnProperty('max') || payloadElement.hasOwnProperty('min')) {
              if (
                get(payloadElement, 'max.days') === 'today'
                && (today > parsedDate)
              ) {
                returnObject.className += get(payloadElement, 'max.cssClass');
                return returnObject;
              } else if (today <= parsedDate && Difference_In_Days <= get(payloadElement, 'min.days')) {
                returnObject.className += get(payloadElement, 'min.cssClass');
                return returnObject;
              } else {
                return returnObject;
              }
            }
          } else {
            return null;
          }
        }
      case 'arrayContent':
        const valueFromData = this.getValueByKey(key, payloadValue);
        const valueArray = valueFromData?valueFromData:get(keyElement,'defaultValue');
        const returnContent: any = { };
        let htmlContent = '';
        const date  = get(payloadElement, 'itemDatePath', '');
        if (Array.isArray(valueArray)) {
          const category  = get(payloadElement, 'itemValuePath', '');
          const itemCss  = get(payloadElement, 'itemClass', 'itemClass');
          returnContent.title = valueArray.length;
          if (returnContent.title > 0) {
            htmlContent = '<ul class ="' + get(payloadElement, 'listClass', 'list') + '"</ul>';
            valueArray.forEach(item => {
              const parsedDate = new Date(get(item, date));
              htmlContent +=  '<li class = "' + itemCss + '">' + get(item, category) + ' - ' + parsedDate.toDateString() + '</li>' ;
            });
          } else {
            htmlContent = 'None';
          }
        } else {
          returnContent.title = get(payloadElement, 'emptytitle', 'No');
        }
        returnContent.htmlContent = htmlContent;
        return returnContent;
      case 'arrayContentOrgList':
        const valueArr = this.getValueByKey(key, payloadValue);
        const retContent: any = { };
        if (Array.isArray(valueArr)) {
          if (valueArr.length > 0) {
            const itemCss = get(payloadElement, 'itemClass', 'itemClass');
            let orgIdsArr = '';
            valueArr.forEach(item => {
              orgIdsArr += `${item.reference}` + ',';
            });
            orgIdsArr = orgIdsArr.substring(0, orgIdsArr.length - 1);
            const queryString = `organization?_id=${orgIdsArr}&_sort:asc=name|transform=concatinatedDisplayName:display,id:code`;
            this.searchService.query(queryString, '', { }, false, { }, true).pipe(map((res) => {
              if (!isEmpty(res)) {
                retContent.title = (valueArr.length - 1 > 0) ? `${res[0].name}+${valueArr.length - 1}` : `${res[0].name}`;

                let htmlContent = '<ul class ="' + get(payloadElement, 'listClass', 'list') + '"</ul>';
                // res.forEach(item => {
                //   htmlContent += '<li class = "' + itemCss + '">' + `${item.name}` + '</li>';
                // });
                retContent.htmlContent = htmlContent;
                this.changeDetectorRef.markForCheck();
                this.changeDetectorRef.detectChanges();
              }
            })).subscribe();
          }
          else {
            retContent.htmlContent = 'None';
          }
        } else {
          retContent.title = get(payloadElement, 'emptytitle', 'No');
          retContent.htmlContent = '';
        }
        return retContent;
      case 'arrayContentIcon':
        let returnContentIcon:any = { };
        let cssClassHide = '';
        let aggIdentifier = {};
        let patientKeyObject = this.patientService.getPatientKeysFromResource(this._value)
        let patientIdKey = patientKeyObject.patientIdKey
        let patientId = this.getValueByKey(patientIdKey, payloadValue);
        returnContentIcon.title = this.getValueByKey(key, payloadValue);
        if(patientId) {
          this.store.select('tablelist', 'list', this._worklistType, 'identifierData').pipe(takeUntil(this._destroyer$.asObservable())).subscribe((identifierData) => {
            if(patientId in identifierData) {
              aggIdentifier = (identifierData[patientId]).aggIdentifier
            }
          });
        }
          cssClassHide = get(payloadElement, 'className', '');
          let identifiertoolTip = this.patientService.getIdentifierToolTip(aggIdentifier)
          this.showMultiTooltip = identifiertoolTip.showTooltip
          returnContentIcon.htmlContentIcon = identifiertoolTip.htmlContentIcon

        // }
        returnContentIcon.cssClass = cssClassHide;
        return returnContentIcon;
      case 'text':
        if(key=='MRN'){
        return {
          className: 'hide-value'
        };
      }
      case 'boolean-indication':
        return Object.assign({ }, payloadElement, { 'className': extraCssClass});
      case 'appointment':
        const myObject = {
          className: extraCssClass
        };
        if (!!rowDataToCheck) {
          myObject.className += this.getClassByRowData(rowDataToCheck);
        }
        return myObject;
      case 'age':
        const returnObj: any = { className: extraCssClass };
        const dobField  = get(payloadElement, 'dateValuePath', 'DiagnosticOrder#Patient#birthDate');
        const itemValue =   this.getValueByKey(dobField, payloadValue);
        returnObj['className'] = get(payloadElement, 'itemClass', 'ageClass') + ' ' + extraCssClass ;
        const age =  moment().diff(itemValue, 'years');
        returnObj['value'] = age;
        return returnObj;
      default:
        return {
          className: extraCssClass,
          workFlowStatus: isWorkflowCompleted
        };
    }
  }

  private valueNotFound(value: string) {
    return (value === null || value === 'undefined' || value === '' ||  value === undefined);
  }

  private updateToDefaultValue(config: IKeyElement) {
    return config.defaultValue ? config.defaultValue : '';
  }

  escapeTag(tag: string): string {
    return StringUtils.escapeHtml(tag);
  }

  getTextValue(parsedValue: any) {
    parsedValue.payloadValue.hasEllipsis = false;
    if (has(parsedValue.payloadValue, 'useEllipsis') && this.searchText.length === 0) {
      const useEllipsis = get(parsedValue.payloadValue, 'useEllipsis', false);
      const maxTextLength = get(parsedValue.payloadValue, 'textLength', undefined);
      const valueToAnalyze = this.escapeChevron(parsedValue.value || parsedValue.payload);
      parsedValue.payloadValue.hasEllipsis = useEllipsis && !!maxTextLength && valueToAnalyze.length > maxTextLength;
      return (parsedValue.payloadValue.hasEllipsis) ? valueToAnalyze.substring(0, maxTextLength).concat('...') : valueToAnalyze;
    }
    return this.escapeChevron(parsedValue.value || parsedValue.payload);
  }

  getTextTooltipDisplayStatus(parsedValue: any){
    return parsedValue.payloadValue.hasEllipsis && this.searchText.length === 0;
  }
}
